<template>
  <AlertDialog v-model:open="open">
    <AlertDialogTrigger as-child>
      <slot name="trigger" />
    </AlertDialogTrigger>
    <AlertDialogContent class="tw:!max-w-md tw:gap-6">
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{ $t('Publish on a third-party service') }}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {{ $t('Select a service to publish the selected reviews.') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <fieldset :disabled="loading" class="tw:space-y-4">
        <div class="tw:flex tw:flex-col tw:gap-2">
          <label for="publicationThirdPartyService" class="tw:text-sm tw:font-medium">{{ $t('Select a service') }}</label>
          <select class="tw:border tw:border-zinc-300 tw:rounded-md tw:px-3 tw:py-2 tw:text-sm"
                  v-model="selectedThirdPartyService"
                  id="publicationThirdPartyService">
            <option value="">----</option>
            <option v-for="service in publicationThirdPartyServices"
                    :key="service.id ?? service.name"
                    :value="service">
              {{ service.name }}
            </option>
          </select>
        </div>
        <div class="tw:flex tw:items-center tw:gap-2">
          <input type="checkbox"
                 id="publicationThirdPartyServiceUpdateStatus"
                 v-model="updateStatus"
                 class="tw:h-4 tw:w-4">
          <label for="publicationThirdPartyServiceUpdateStatus"
                 class="tw:text-sm">
            {{ $t('Update status to “VALIDATED”') }}
          </label>
        </div>
      </fieldset>
      <AlertDialogFooter>
        <AlertDialogCancel as-child>
          <Button variant="secondary" :disabled="loading">{{ $t('Cancel') }}</Button>
        </AlertDialogCancel>
        <Button :disabled="!selectedThirdPartyService || loading"
                :loading="loading"
                @click.prevent="publishReviews">
          {{ $t('Publish reviews') }}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcn/components/ui/alert-dialog";
import { Button } from "@/shadcn/components/ui/button";
import { useBulkActionApi } from "@/composables/useBulkActionApi";
import { toast } from "vue-sonner";
import { useI18n } from "vue-i18n";

interface ThirdPartyService {
  id: number;
  name: string;
}

const props = withDefaults(defineProps<{
  publicationThirdPartyServices: ThirdPartyService[];
  items: (string | number)[];
}>(), {
  publicationThirdPartyServices: () => [],
  items: () => [],
});

const emit = defineEmits<{
  (e: 'published'): void;
}>();

const { t } = useI18n();

const open = ref(false);
const selectedThirdPartyService = ref<ThirdPartyService | null>(null);
const updateStatus = ref(true);

const { loading, execute: executeBulkAction } = useBulkActionApi('/backoffice/api/bulk-action');

const publishReviews = async () => {
  if (!selectedThirdPartyService.value || loading.value) {
    return;
  }

  const response = await executeBulkAction(
    'publish-review-to-third-party',
    props.items,
    {
      third_party_service_id: selectedThirdPartyService.value.id,
      update_status: updateStatus.value,
    }
  );

  if (response.errors && response.errors.length > 0) {
    response.errors.forEach((error) => {
      toast.error(error);
    });
  }

  if (response.processed && response.processed.length > 0) {
    toast.success(t("{count} review(s) published successfully", { count: response.processed.length }));
  }

  open.value = false;
  emit('published');
};
</script>

